/**
 * Google Identity Services (OAuth2 token) para login web.
 * Mesmo webClientId do Guardian (GOOGLE_WEB_CLIENT_ID).
 */

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "432867419450-3ua8ad12ulla4rae600dmg4c126ntlrj.apps.googleusercontent.com";

let gisLoadPromise = null;

function loadGisScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Auth indisponível neste ambiente."));
  }
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve(window.google);
  }
  if (gisLoadPromise) return gisLoadPromise;

  gisLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () =>
        reject(new Error("Falha ao carregar Google Identity Services."))
      );
      if (window.google?.accounts?.oauth2) resolve(window.google);
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () =>
      reject(new Error("Falha ao carregar Google Identity Services."));
    document.head.appendChild(script);
  });

  return gisLoadPromise;
}

/**
 * Abre o seletor de conta Google e devolve perfil + id.
 * @returns {Promise<{ email: string, full_name: string, google_id: string, photo_url?: string }>}
 */
export async function requestGoogleProfile() {
  if (!GOOGLE_WEB_CLIENT_ID) {
    throw new Error("Client ID do Google não configurado (REACT_APP_GOOGLE_CLIENT_ID).");
  }

  const google = await loadGisScript();

  const accessToken = await new Promise((resolve, reject) => {
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_WEB_CLIENT_ID,
        scope: "openid email profile",
        prompt: "select_account",
        callback: (response) => {
          if (response?.error) {
            reject(
              new Error(
                response.error_description ||
                  response.error ||
                  "Login Google cancelado."
              )
            );
            return;
          }
          if (!response?.access_token) {
            reject(new Error("Token Google não recebido."));
            return;
          }
          resolve(response.access_token);
        },
        error_callback: (err) => {
          reject(
            new Error(
              err?.message || "Não foi possível concluir o login com Google."
            )
          );
        },
      });
      client.requestAccessToken();
    } catch (err) {
      reject(err);
    }
  });

  const userRes = await fetch("https://www.googleapis.com/userinfo/v2/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!userRes.ok) {
    throw new Error("Não foi possível obter os dados da conta Google.");
  }
  const profile = await userRes.json();
  if (!profile?.email || !profile?.id) {
    throw new Error("Conta Google sem e-mail ou identificador.");
  }

  return {
    email: String(profile.email).toLowerCase().trim(),
    full_name: profile.name || profile.email,
    google_id: String(profile.id),
    photo_url: profile.picture || undefined,
  };
}
